import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { PersonFile } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { ageOn, dayText, fileFormOf, fileIsEmpty, filePayload, fileProblems, telHref, type FileForm } from '../../data/staff.ts'
import { localDate } from '../../lib/time.ts'
import { Banner, Field } from '../../ui/components.tsx'
import { StepUpField, useSensitive } from '../../ui/sensitive.tsx'

/** The information sheet (fiche de renseignements, M20 decision 6): the direction's alone (contact.manage), read online after a fresh second-factor code through the
 *  audited `api.read_person_file` and kept only in this component's state (never in the device's mirror, never in the outbox once answered: sync/transient.ts).
 *  Saving is person_file.set@1 with only what changed; the sheet is read again after it (M5: a page that shows server-only data reloads it after its own change). */
export function PersonFileSection({ personId }: { personId: string }) {
  const { t } = useTranslation()
  const { org, deviceId } = useClub()
  const s = useSensitive()
  const [asked, setAsked] = useState(false)
  const [file, setFile] = useState<PersonFile | null>(null)
  const [editing, setEditing] = useState(false)
  const load = async () => {
    setAsked(true)
    const r = await s.call<PersonFile>('read_person_file', { p_org: org.org_id, p_person: personId, p_device_id: deviceId })
    if (r) setFile(r)
  }
  return (
    <div className="flex flex-col gap-3 border-t pt-4" data-testid="person-file">
      <h3 className="text-lg font-bold">{t('personFile.title')}</h3>
      <p className="meta">{t('personFile.help')}</p>
      {/* the code field appears once asked (a fresh proof needs none): another section's field stays the first one on the page */}
      {(asked || s.error) && !editing && <StepUpField s={s} />}
      {!file ? (
        <button className="btn self-start" disabled={s.busy || !s.online} onClick={() => void load()} data-testid="file-show">{t('personFile.show')}</button>
      ) : editing ? (
        <FileEditor personId={personId} file={file} s={s} onCancel={() => setEditing(false)} onSaved={async () => { setEditing(false); await load(); s.setOk(t('personFile.saved')) }} />
      ) : (
        <>
          {fileIsEmpty(file) ? <p className="text-sm" data-testid="file-empty">{t('personFile.empty')}</p> : <FileView file={file} />}
          <div className="flex flex-wrap gap-2">
            <button className="btn btn-primary" onClick={() => { s.reset(); setEditing(true) }} data-testid="file-edit">{t('personFile.edit')}</button>
            <button className="btn" onClick={() => { setFile(null); setAsked(false); s.reset() }} data-testid="file-hide">{t('personFile.hide')}</button>
          </div>
        </>
      )}
    </div>
  )
}

function FileView({ file }: { file: PersonFile }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const today = localDate(new Date(), site.timezone)
  const notSet = <span className="opacity-60">{t('personFile.notSet')}</span>
  const sizes = [file.clothing_top && `${t('personFile.top')} ${file.clothing_top}`, file.clothing_bottom && `${t('personFile.bottom')} ${file.clothing_bottom}`, file.shoe_size && `${t('personFile.shoe')} ${file.shoe_size}`].filter(Boolean)
  return (
    <div className="flex flex-col gap-3" data-testid="file-view">
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-[auto_1fr]">
        <dt className="font-semibold">{t('personFile.birth')}</dt>
        <dd data-testid="file-view-birth">{file.birth_date ? <>{dayText(file.birth_date)} <span className="opacity-70">({t('personFile.age', { count: ageOn(file.birth_date, today) })})</span></> : notSet}</dd>
        <dt className="font-semibold">{t('personFile.sizes')}</dt>
        <dd>{sizes.length ? sizes.join(' · ') : notSet}</dd>
        <dt className="font-semibold">{t('personFile.phone')}</dt>
        <dd>{file.contact.phone ? <a className="font-semibold underline" href={telHref(file.contact.phone)} data-testid="file-tel">{file.contact.phone}</a> : notSet}</dd>
        <dt className="font-semibold">{t('personFile.email')}</dt>
        <dd className="break-all">{file.contact.email ?? notSet}</dd>
        <dt className="font-semibold">{t('personFile.address')}</dt>
        <dd>{file.contact.address ?? notSet}</dd>
      </dl>
      <div>
        <p className="font-semibold">{t('personFile.emergencyTitle')}</p>
        {file.emergency.length === 0 ? <p className="meta">{t('personFile.noEmergency')}</p> : (
          <ul className="flex flex-col gap-2" data-testid="file-view-emergency">
            {file.emergency.map((c) => (
              <li key={c.rank} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-slate-100 p-3 dark:bg-night">
                <span><span className="font-semibold">{c.name}</span> <span className="opacity-75">({c.relationship})</span></span>
                <a className="btn !px-4" href={telHref(c.phone)} data-testid="file-tel">{c.phone}</a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function FileEditor({ personId, file, s, onCancel, onSaved }: { personId: string; file: PersonFile; s: ReturnType<typeof useSensitive>; onCancel: () => void; onSaved: () => Promise<void> }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const today = localDate(new Date(), site.timezone)
  const was = fileFormOf(file)
  const [f, setF] = useState<FileForm>(was)
  const problems = fileProblems(f, was, today)
  const set = (k: Exclude<keyof FileForm, 'emergency'>) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value })
  const line = (i: number, k: 'name' | 'relationship' | 'phone', v: string) => setF({ ...f, emergency: f.emergency.map((l, j) => (j === i ? { ...l, [k]: v } : l)) })
  const id = (k: string) => `pf-${k}-${personId}`
  return (
    <form className="flex flex-col gap-4" data-testid="file-form" onSubmit={(e) => {
      e.preventDefault()
      const payload = filePayload(personId, f, was)
      if (!payload) { s.setOk(t('personFile.nothing')); return }
      void s.command('person_file.set@1', payload, 'personFile.saved').then((ok) => { if (ok) void onSaved() })
    }}>
      <Field label={t('personFile.birth')} error={problems.birth_date ? t(`personFile.check.${problems.birth_date}`) : null} htmlFor={id('birth')}>
        <input id={id('birth')} type="date" className="input" max={today} value={f.birth_date} onChange={set('birth_date')} data-testid="file-birth" />
      </Field>
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 font-semibold">{t('personFile.sizes')}</legend>
        <div className="grid grid-cols-3 gap-2">
          <Field label={t('personFile.top')} htmlFor={id('top')}><input id={id('top')} className="input" maxLength={10} value={f.clothing_top} onChange={set('clothing_top')} data-testid="file-top" /></Field>
          <Field label={t('personFile.bottom')} htmlFor={id('bottom')}><input id={id('bottom')} className="input" maxLength={10} value={f.clothing_bottom} onChange={set('clothing_bottom')} data-testid="file-bottom" /></Field>
          <Field label={t('personFile.shoe')} htmlFor={id('shoe')}><input id={id('shoe')} className="input" maxLength={10} inputMode="decimal" value={f.shoe_size} onChange={set('shoe_size')} data-testid="file-shoe" /></Field>
        </div>
      </fieldset>
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 font-semibold">{t('personFile.emergencyTitle')}</legend>
        <p className="meta">{t('personFile.emergencyHelp')}</p>
        {f.emergency.map((l, i) => (
          <div key={i} className="flex flex-col gap-2 rounded-xl border border-slate-200 p-3 dark:border-night-line" data-testid="file-em-line">
            <p className="text-sm font-semibold">{t('personFile.line', { n: i + 1 })}</p>
            <Field label={t('personFile.emName')} htmlFor={id(`emn${i}`)}><input id={id(`emn${i}`)} className="input" maxLength={120} autoComplete="off" value={l.name} onChange={(e) => line(i, 'name', e.target.value)} data-testid={`file-em-name-${i + 1}`} /></Field>
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label={t('personFile.emRelation')} htmlFor={id(`emr${i}`)}><input id={id(`emr${i}`)} className="input" maxLength={60} autoComplete="off" value={l.relationship} onChange={(e) => line(i, 'relationship', e.target.value)} data-testid={`file-em-rel-${i + 1}`} /></Field>
              <Field label={t('personFile.emPhone')} htmlFor={id(`emp${i}`)}><input id={id(`emp${i}`)} className="input" type="tel" maxLength={40} autoComplete="off" value={l.phone} onChange={(e) => line(i, 'phone', e.target.value)} data-testid={`file-em-phone-${i + 1}`} /></Field>
            </div>
          </div>
        ))}
        {problems.emergency && <p role="alert" className="text-sm font-medium text-red-700 dark:text-red-300">{t(`personFile.check.${problems.emergency}`)}</p>}
      </fieldset>
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 font-semibold">{t('personFile.contactTitle')}</legend>
        <Field label={t('personFile.phone')} htmlFor={id('phone')}><input id={id('phone')} className="input" type="tel" maxLength={40} autoComplete="off" value={f.phone} onChange={set('phone')} data-testid="file-phone" /></Field>
        <Field label={t('personFile.email')} htmlFor={id('email')}><input id={id('email')} className="input" type="email" maxLength={200} autoComplete="off" value={f.email} onChange={set('email')} data-testid="file-email" /></Field>
        <Field label={t('personFile.address')} htmlFor={id('address')}><input id={id('address')} className="input" maxLength={300} autoComplete="off" value={f.address} onChange={set('address')} data-testid="file-address" /></Field>
      </fieldset>
      <StepUpField s={s} />
      <div className="flex flex-wrap gap-2">
        <button className="btn btn-primary" disabled={s.busy || !s.online || Object.keys(problems).length > 0} data-testid="file-save">{t('personFile.save')}</button>
        <button type="button" className="btn" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </form>
  )
}
